import React, { useState, useEffect, useRef } from 'react'
import { productApi, ProductModels } from '@getgreenline/products'
import { Alert } from 'reactstrap'
import { Routes } from '../../../../../constants/Routes'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { Link } from 'react-router'
import { message } from 'antd-v4'
import { ProductStore } from '../../../../../stores/CurrentCompanyStore/ProductStore'
import { usePrevious } from '../../../../../utilities/hooks/usePrevious'
import { IGlobalProduct } from '@getgreenline/global-products'
import { getSkuFromGlobalProduct } from '../../../../../utilities/globalProduct'

interface Props {
  products: IGlobalProduct[]
  handleRemove: (index: number) => void
  currentCompanyStore?: CurrentCompanyStore
}

export const ImportGlobalProductsPreview = React.memo(
  ({ products, handleRemove, currentCompanyStore }: Props) => {
    if (!currentCompanyStore?.productStore) return null
    const companyId = currentCompanyStore.productStore.company.id
    const [duplicateSkuProducts, setDuplicateSkuProducts] = useState<
      Map<string, ProductModels.IChildProduct>
    >(new Map())
    const cartContainer = useRef<HTMLDivElement>(null)
    const prevProductCount = usePrevious(products.length) || 0

    const getDuplicateSkuProduct = async (
      sku?: string,
    ): Promise<ProductModels.IChildProduct | undefined> => {
      if (!sku) return
      if (duplicateSkuProducts.has(sku.toLowerCase())) return
      const { products } = await productApi.getProducts(companyId, { sku, limit: 10 })
      if (!products) return
      const productsFlat = ProductStore.flattenProductsV2(products)
      return productsFlat.find((product) => product.sku?.toLowerCase() === sku.toLowerCase())
    }

    const resetDuplicateSkuProducts = () => {
      duplicateSkuProducts.clear()
      setDuplicateSkuProducts(new Map())
    }

    const removeDuplicateSkuProduct = (sku: string) => {
      const duplicateSkuProduct = duplicateSkuProducts.get(sku.toLowerCase())
      if (!duplicateSkuProduct?.sku) return
      duplicateSkuProducts.delete(sku.toLowerCase())
      setDuplicateSkuProducts(new Map(duplicateSkuProducts))
    }

    const addDuplicateSkuProduct = (product?: ProductModels.IChildProduct) => {
      if (!product?.sku) return
      if (duplicateSkuProducts.has(product.sku.toLowerCase())) return
      duplicateSkuProducts.set(product.sku.toLowerCase(), product)
      setDuplicateSkuProducts(new Map(duplicateSkuProducts))
    }

    const scrollToFocussedProduct = (htmlElement: HTMLElement | null) => {
      if (htmlElement) {
        htmlElement.scrollTop = htmlElement.scrollHeight
      }
    }

    const removeProduct = async (index: number) => {
      const product = products[index]
      const sku = getSkuFromGlobalProduct(product, currentCompanyStore.company)
      if (sku) removeDuplicateSkuProduct(sku)
      handleRemove(index)
    }

    useEffect(() => {
      if (products.length === prevProductCount) {
        resetDuplicateSkuProducts()
      }
      if (products.length < prevProductCount) return
      const sku = getSkuFromGlobalProduct(
        products[products.length - 1],
        currentCompanyStore.company,
      )
      if (!sku) return

      getDuplicateSkuProduct(sku)
        .then(addDuplicateSkuProduct)
        .catch((error) => {
          console.error(error)
          message.error(
            `Something went wrong while checking for a SKU matching ${sku} in your product list.`,
          )
        })

      scrollToFocussedProduct(cartContainer.current)
    }, [products.length, getSkuFromGlobalProduct(products[0])])

    return (
      <>
        <div ref={cartContainer} style={{ maxHeight: '35vh', overflowX: 'auto' }}>
          {products.map((product, index) => {
            const sku = getSkuFromGlobalProduct(product, currentCompanyStore.company)

            return (
              <div className='d-flex mb-2' key={`ProductToAdd-${sku}-${product.barcode}-${index}`}>
                <div style={{ width: '100%' }}>
                  <div className='text-muted'>
                    <small>SKU: {sku}</small>
                  </div>
                  <div>{product.name}</div>
                  <span
                    style={{ color: 'blue', cursor: 'pointer' }}
                    onClick={() => removeProduct(index)}
                  >
                    Remove
                  </span>
                </div>
              </div>
            )
          })}
        </div>
        {duplicateSkuProducts?.size > 0 && (
          <Alert color='warning'>
            <span>
              SKU{duplicateSkuProducts.size === 1 ? '' : 's'}{' '}
              {[...duplicateSkuProducts.values()].map(({ parentProductId, id, sku }, index) => (
                <span key={index}>
                  <Link
                    to={Routes.EDIT_PRODUCT(companyId, parentProductId || id)}
                    target='_blank'
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                    rel='noreferrer'
                  >
                    <span style={{ paddingRight: '1%' }}>{sku}</span>
                  </Link>
                  {index + 1 !== duplicateSkuProducts.size && <span>, </span>}
                </span>
              ))}
              already exist{duplicateSkuProducts.size === 1 ? 's' : ''} in your product catalog.
            </span>
          </Alert>
        )}
      </>
    )
  },
)
